'use client'

import { useState } from 'react'
import { useLocale } from '@/lib/i18n/use-locale'
import { QRCodeModal } from './qr-code-modal'

interface UrlResultProps {
  result: {
    shortUrl: string
    shortCode: string
    originalUrl: string
    expiresAt: string | null
    faviconUrl?: string | null
  }
}

export function UrlResult({ result }: UrlResultProps) {
  const [copied, setCopied] = useState(false)
  const [showQRModal, setShowQRModal] = useState(false)
  const { t } = useLocale()

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(result.shortUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  const shareUrl = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Shortened URL',
          text: `Check out this link: ${result.shortUrl}`,
          url: result.shortUrl,
        })
      } catch (err) {
        if ((err as Error).name !== 'AbortError') {
          console.error('Failed to share:', err)
        }
      }
    } else {
      copyToClipboard()
    }
  }

  return (
    <>
      <div className="mt-8 p-6 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          {t('urlResult.ready')}
        </h3>

        <div className="flex items-center space-x-2 mb-4">
          <input
            type="text"
            value={result.shortUrl}
            readOnly
            className="flex-1 px-4 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg font-mono text-sm text-gray-900 dark:text-white"
          />
          <button
            onClick={copyToClipboard}
            className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition whitespace-nowrap"
          >
            {copied ? t('urlResult.copied') : t('urlResult.copy')}
          </button>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
          <a
            href={result.shortUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 font-medium rounded-lg transition text-center"
          >
            {t('urlResult.visit')}
          </a>
          <button
            onClick={() => setShowQRModal(true)}
            className="px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 font-medium rounded-lg transition"
          >
            {t('urlResult.qr')}
          </button>
          <button
            onClick={shareUrl}
            className="px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 font-medium rounded-lg transition"
          >
            {t('urlResult.share')}
          </button>
          <button
            onClick={copyToClipboard}
            className="px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 font-medium rounded-lg transition"
          >
            {copied ? t('urlResult.copied') : t('urlResult.copyLink')}
          </button>
        </div>

        <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
          <p className="flex items-start gap-2">
            <span className="font-medium whitespace-nowrap">{t('urlResult.original')}</span>
            <span className="flex items-center gap-2 break-all">
              {result.faviconUrl && (
                <img
                  src={result.faviconUrl}
                  alt=""
                  className="w-4 h-4 flex-shrink-0"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none'
                  }}
                />
              )}
              {result.originalUrl}
            </span>
          </p>
          <p>
            <span className="font-medium">{t('urlResult.shortCode')}</span> {result.shortCode}
          </p>
          {result.expiresAt && (
            <p>
              <span className="font-medium">{t('urlResult.expires')}</span>{' '}
              {new Date(result.expiresAt).toLocaleString()}
            </p>
          )}
          <p className="pt-2">
            <a
              href={`/analytics/${result.shortCode}`}
              className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 font-medium"
            >
              {t('urlResult.analytics')}
            </a>
          </p>
        </div>
      </div>

      {showQRModal && (
        <QRCodeModal
          shortCode={result.shortCode}
          shortUrl={result.shortUrl}
          onClose={() => setShowQRModal(false)}
        />
      )}
    </>
  )
}
